import { useWsEvents } from '../hooks/useWsEvents';
import { useState, useEffect, useRef } from 'react';
import type { TimerData } from '../types/widgets';
import { fonts, glass, glassBorder, brandGradient, semantic, palette } from '../styles/overlayTokens';

const INITIAL: TimerData = { startedAt: null, duration: 1500, isRunning: false };

export function RoundTimer() {
  const params = new URLSearchParams(window.location.search);
  const useFallback = params.get('fallback') === 'true';

  const { data } = useWsEvents<TimerData>('timer', INITIAL);
  const [display, setDisplay] = useState('25:00');
  const [urgency, setUrgency] = useState<'normal' | 'warning' | 'critical' | 'expired'>('normal');
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  useEffect(() => {
    clearInterval(intervalRef.current);

    const tick = () => {
      let remaining: number;

      if (data.isRunning && data.startedAt) {
        const elapsed = Math.floor((Date.now() - data.startedAt) / 1000);
        remaining = Math.max(0, data.duration - elapsed);
      } else {
        remaining = data.duration;
      }

      const min = Math.floor(remaining / 60);
      const sec = remaining % 60;
      setDisplay(`${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`);

      if (remaining === 0) setUrgency('expired');
      else if (remaining <= 60) setUrgency('critical');
      else if (remaining <= 300) setUrgency('warning');
      else setUrgency('normal');
    };

    if (data.isRunning) {
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      // Paused — compute display once without starting interval
      const remaining = data.duration;
      const min = Math.floor(remaining / 60);
      const sec = remaining % 60;
      setDisplay(`${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`);
      if (remaining === 0) setUrgency('expired');
      else if (remaining <= 60) setUrgency('critical');
      else if (remaining <= 300) setUrgency('warning');
      else setUrgency('normal');
    }

    return () => clearInterval(intervalRef.current);
  }, [data.startedAt, data.duration, data.isRunning]);

  const color =
    urgency === 'expired' ? palette.brilliantRose :
    urgency === 'critical' ? semantic.error :
    urgency === 'warning' ? semantic.warning :
    '#ffffff';

  const pulseClass =
    urgency === 'critical' ? 'pulse-critical' :
    urgency === 'warning' ? 'pulse-warning' :
    '';

  const bgStyle = useFallback
    ? { background: '#121216' }
    : { background: glass.timer, backdropFilter: 'blur(16px) saturate(1.2)' };

  return (
    <div style={{
      width: 240,
      height: 120,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
    }}>
      <div style={{
        position: 'relative',
        ...bgStyle,
        borderRadius: 16,
        border: `1px solid ${glassBorder.subtle}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {/* Top accent bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: brandGradient.horizontal,
        }} />

        <div
          className={pulseClass}
          style={{
            fontSize: 38,
            fontWeight: 700,
            fontFamily: fonts.display,
            color,
            transition: 'color 0.3s',
            willChange: 'opacity',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {display}
        </div>
      </div>
    </div>
  );
}
